// Watchtower API functions for Supabase integration

import { createClient } from '../supabase/client';
import { slugifyWatchtowerTitle, uniqueWatchtowerSlug } from '../watchtower/slug';
import {
  WatchtowerPost,
  WatchtowerPostWithAuthor,
  WatchtowerComment,
  WatchtowerCommentWithAuthor,
  WatchtowerSearchResult,
  CreatePostRequest,
  UpdatePostRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  SearchPostsRequest,
  GetPostsRequest,
  WATCHTOWER_TABLES,
} from '../types/watchtower';

// Helper function to get Supabase client
const getSupabaseClient = () => {
  if (typeof window === 'undefined') {
    throw new Error('This function can only be called on the client side');
  }
  return createClient();
};

// Helper function to check if user is admin (calls server endpoint)
const isAdmin = async (): Promise<boolean> => {
  try {
    const res = await fetch('/api/auth/check-admin');
    const data = await res.json();
    return !!data.isAdmin;
  } catch {
    return false;
  }
};

// Posts API
export const watchtowerPosts = {
  // Get all posts with author metadata
  async getAll(params: GetPostsRequest = {}): Promise<WatchtowerPostWithAuthor[]> {
    const { limit = 20, offset = 0, featured_only = false, published_only = true, tags } = params;
    const supabase = getSupabaseClient();
    
    let query = supabase
      .from('watchtower_posts_with_author')
      .select('*')
      .order('published_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (published_only) {
      query = query.eq('is_published', true);
    }

    if (featured_only) {
      query = query.eq('is_featured', true);
    }

    if (tags && tags.length > 0) {
      query = query.overlaps('tags', tags);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Get single post by ID
  async getById(id: string): Promise<WatchtowerPostWithAuthor | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('watchtower_posts_with_author')
      .select('*')
      .eq('id', id)
      .eq('is_published', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows found
      throw error;
    }
    return data;
  },

  // Create new post (admin only)
  async create(post: CreatePostRequest): Promise<WatchtowerPost> {
    const isAdminUser = await isAdmin();
    if (!isAdminUser) throw new Error('Only administrators can create posts');

    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    // Readable URL: slug from the title, made unique against existing posts.
    // Set once here; later title edits keep the slug so links never break.
    const baseSlug = slugifyWatchtowerTitle(post.title);
    const { data: clashes } = await supabase
      .from(WATCHTOWER_TABLES.POSTS)
      .select('slug')
      .like('slug', `${baseSlug}%`);
    const taken = new Set((clashes || []).map((row: { slug: string | null }) => row.slug).filter(Boolean) as string[]);

    // Prepare the post data, handling both single and multiple images
    const postData: any = {
      ...post,
      slug: uniqueWatchtowerSlug(baseSlug, taken),
      author_id: user.user.id,
      tags: post.tags || [],
    };

    // Handle images - only use image_urls array
    if (post.image_urls && post.image_urls.length > 0) {
      postData.image_urls = post.image_urls;
      // Set image_url to first image for backward compatibility
      postData.image_url = post.image_urls[0];
    }
    // If no images, don't add image fields at all

    const { data, error } = await supabase
      .from(WATCHTOWER_TABLES.POSTS)
      .insert(postData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update post (admin only)
  async update(post: UpdatePostRequest): Promise<WatchtowerPost> {
    const isAdminUser = await isAdmin();
    if (!isAdminUser) throw new Error('Only administrators can update posts');

    const { id, ...postData } = post;
    const supabase = getSupabaseClient();
    
    // Prepare the update data, handling multiple images
    const updateData: any = {
      ...postData,
      updated_at: new Date().toISOString(),
    };

    // Handle images - only use image_urls array
    if (postData.image_urls && postData.image_urls.length > 0) {
      updateData.image_urls = postData.image_urls;
      // Set image_url to first image for backward compatibility
      updateData.image_url = postData.image_urls[0];
    }
    // If no images, don't add image fields at all
    
    const { data, error } = await supabase
      .from(WATCHTOWER_TABLES.POSTS)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete post (admin only)
  async delete(id: string): Promise<void> {
    const isAdminUser = await isAdmin();
    if (!isAdminUser) throw new Error('Only administrators can delete posts');

    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(WATCHTOWER_TABLES.POSTS)
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  // Increment view count
  async incrementViewCount(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase.rpc('increment_post_view_count', {
      post_uuid: id
    });

    if (error) throw error;
  },

  // Search posts
  async search(params: SearchPostsRequest): Promise<WatchtowerSearchResult[]> {
    const { query, limit = 20, offset = 0 } = params;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.rpc('search_watchtower_posts', {
      search_query: query,
      limit_count: limit,
      offset_count: offset
    });

    if (error) throw error;
    return data || [];
  },

  // Get featured posts
  async getFeatured(limit = 5): Promise<WatchtowerPostWithAuthor[]> {
    return this.getAll({ featured_only: true, limit });
  },

  // Get posts by tags
  async getByTags(tags: string[], limit = 20): Promise<WatchtowerPostWithAuthor[]> {
    return this.getAll({ tags, limit });
  },
};

// Comments API
export const watchtowerComments = {
  // Get comments for a post
  async getByPostId(postId: string): Promise<WatchtowerCommentWithAuthor[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('watchtower_comments_with_author')
      .select('*')
      .eq('post_id', postId)
      .eq('is_approved', true)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Create comment
  async create(comment: CreateCommentRequest): Promise<WatchtowerComment> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from(WATCHTOWER_TABLES.COMMENTS)
      .insert({
        ...comment,
        author_id: user.user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update comment
  async update(comment: UpdateCommentRequest): Promise<WatchtowerComment> {
    const { id, ...updateData } = comment;
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
      .from(WATCHTOWER_TABLES.COMMENTS)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete comment (admin can delete any comment, users can only delete their own)
  async delete(id: string): Promise<void> {
    const supabase = getSupabaseClient();
    const isAdminUser = await isAdmin();
    const { data: user } = await supabase.auth.getUser();
    
    if (!isAdminUser && user.user) {
      // Non-admin users can only delete their own comments
      // Check if comment belongs to user
      const { data: comment, error: checkError } = await supabase
        .from(WATCHTOWER_TABLES.COMMENTS)
        .select('author_id')
        .eq('id', id)
        .single();
      
      if (checkError) {
        throw new Error(`Failed to check comment ownership: ${checkError.message}`);
      }
      
      if (!comment || comment.author_id !== user.user.id) {
        throw new Error('You can only delete your own comments');
      }
    }
    
    // Delete the comment
    // For admin, we can delete directly
    // For regular users, RLS policies will enforce ownership
    const { error } = await supabase
      .from(WATCHTOWER_TABLES.COMMENTS)
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting comment:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
      });
      throw error;
    }
  },
};

// Likes API
export const watchtowerLikes = {
  // Toggle post like
  async togglePostLike(postId: string): Promise<{ liked: boolean; likeCount: number }> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    // Check if already liked
    const { data: existingLike } = await supabase
      .from(WATCHTOWER_TABLES.POST_LIKES)
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.user.id)
      .single();

    if (existingLike) {
      // Unlike
      await supabase
        .from(WATCHTOWER_TABLES.POST_LIKES)
        .delete()
        .eq('post_id', postId)
        .eq('user_id', user.user.id);
    } else {
      // Like
      await supabase
        .from(WATCHTOWER_TABLES.POST_LIKES)
        .insert({
          post_id: postId,
          user_id: user.user.id,
        });
    }

    // Get updated like count
    const { count } = await supabase
      .from(WATCHTOWER_TABLES.POST_LIKES)
      .select('*', { count: 'exact', head: true })
      .eq('post_id', postId);

    return {
      liked: !existingLike,
      likeCount: count || 0,
    };
  },

  // Toggle comment like
  async toggleCommentLike(commentId: string): Promise<{ liked: boolean; likeCount: number }> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    // Check if already liked
    const { data: existingLike } = await supabase
      .from(WATCHTOWER_TABLES.COMMENT_LIKES)
      .select('id')
      .eq('comment_id', commentId)
      .eq('user_id', user.user.id)
      .single();

    if (existingLike) {
      // Unlike
      await supabase
        .from(WATCHTOWER_TABLES.COMMENT_LIKES)
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.user.id);
    } else {
      // Like
      await supabase
        .from(WATCHTOWER_TABLES.COMMENT_LIKES)
        .insert({
          comment_id: commentId,
          user_id: user.user.id,
        });
    }

    // Get updated like count
    const { count } = await supabase
      .from(WATCHTOWER_TABLES.COMMENT_LIKES)
      .select('*', { count: 'exact', head: true })
      .eq('comment_id', commentId);

    return {
      liked: !existingLike,
      likeCount: count || 0,
    };
  },

  // Check if user liked post
  async isPostLiked(postId: string): Promise<boolean> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { data } = await supabase
      .from(WATCHTOWER_TABLES.POST_LIKES)
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', user.user.id)
      .single();

    return !!data;
  },

  // Check if user liked comment
  async isCommentLiked(commentId: string): Promise<boolean> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { data } = await supabase
      .from(WATCHTOWER_TABLES.COMMENT_LIKES)
      .select('id')
      .eq('comment_id', commentId)
      .eq('user_id', user.user.id)
      .single();

    return !!data;
  },
};

// Subscriptions API
export const watchtowerSubscriptions = {
  // Subscribe to notifications
  async subscribe(email?: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from(WATCHTOWER_TABLES.SUBSCRIPTIONS)
      .upsert({
        user_id: user.user.id,
        email: email || user.user.email,
        is_active: true,
      });

    if (error) throw error;
  },

  // Unsubscribe from notifications
  async unsubscribe(): Promise<void> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from(WATCHTOWER_TABLES.SUBSCRIPTIONS)
      .update({ is_active: false })
      .eq('user_id', user.user.id);

    if (error) throw error;
  },

  // Check subscription status
  async isSubscribed(): Promise<boolean> {
    const supabase = getSupabaseClient();
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { data } = await supabase
      .from(WATCHTOWER_TABLES.SUBSCRIPTIONS)
      .select('is_active')
      .eq('user_id', user.user.id)
      .eq('is_active', true)
      .single();

    return !!data;
  },
};
